import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { get, replace, startCase } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import FlagCountry from 'src/components/common/FlagCountry';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import TooltipTradeDataLimit from 'src/components/common/TooltipTradeDataLimit';
import ExportDataTable from 'src/components/ExportData';
import { EXPORT_TABLE_TYPE, URL_EXPORT_MAPPING } from 'src/components/ExportData/config';
import LinkToCompanyDetail from 'src/components/LinkToCompanyDetail';
import ModalComponent from 'src/components/Modal/ModalComponent';
import CustomTable from 'src/components/Table';
import { CompanyTradeDataTab } from 'src/features/CompanyTradeData';
import { LIST_RESTRICTION_DATA, useProvisionRestriction } from 'src/hooks/useProvisionRestriction';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { TradeDataWithoutHSCodeService } from 'src/services/api/tradeDataWithoutHSCode';
import { FORMAT_DATE_TIME, LIMIT_GET_LIST } from 'src/utils/constants/config';
import { TypeBuyerSupplier } from 'src/utils/constants/enum';
import { LIST_FEATURES } from 'src/utils/constants/featuresPoint';
import { renderNumberOrDefault, renderValueNumberTrade } from 'src/utils/helpers/stringHelpers';
import { formatValueUSD } from 'src/utils/helpers/utilities';
import { useTradeDataSortStore } from 'src/zustand/useTradeDataSortStore';

interface IProps {
  open: boolean;
  handleOpenChange: (open: boolean) => void;
  type: TypeBuyerSupplier;
  payload: Record<string, any>;
  startDate?: string;
  endDate?: string;
  portCode?: string;
  companyId?: string;
}

const PopupBuyersSuppliersList = ({ open, handleOpenChange, type, payload, startDate, endDate, portCode }: IProps) => {
  const {
    palette: { background },
  } = useTheme();
  const { setSort, ctdBuyersSuppliersList } = useTradeDataSortStore();
  const { checkRestriction } = useProvisionRestriction();
  const [params, setParams] = useState({
    offset: 0,
    limit: LIMIT_GET_LIST,
  });

  const queryParams = useQueryParams();
  const { keyword, tab } = queryParams;

  useEffect(() => {
    // Reset params when open modal
    if (!open) {
      setParams({
        offset: 0,
        limit: LIMIT_GET_LIST,
      });
    }
  }, [open]);

  const mergedPayload = {
    ...payload,
    ...params,
    order: ctdBuyersSuppliersList?.sort,
    sort_by: ctdBuyersSuppliersList?.sortBy,
    start_date: startDate || payload.start_date,
    end_date: endDate || payload.end_date,
  };

  const getQueryFn = async () => {
    switch (tab) {
      case CompanyTradeDataTab.Analytics:
        return await TradeDataWithoutHSCodeService.getAnalyticsBuyersSuppliersList(mergedPayload, type);
      case CompanyTradeDataTab.PortsOfDelivery:
        return await TradeDataWithoutHSCodeService.getPODBuyersSuppliersList(mergedPayload, type, portCode);
      case CompanyTradeDataTab.PortsOfOrigin:
        return await TradeDataWithoutHSCodeService.getPOOBuyersSuppliersList(mergedPayload, type, portCode);
      default:
        return null;
    }
  };

  const { isFetching, data } = useQuery({
    queryKey: ['QUERY_BUYERS_SUPPLIERS_LIST_COMPANY_TRADE_DATA_SEARCH_BY_KEY_WORD', mergedPayload],
    refetchOnWindowFocus: false,
    enabled: open,
    queryFn: getQueryFn,
  });

  const exportEndpoint = useMemo(() => {
    const isBuyer = type === TypeBuyerSupplier.buyer;
    switch (tab) {
      case CompanyTradeDataTab.Analytics:
        return get(
          URL_EXPORT_MAPPING,
          isBuyer ? EXPORT_TABLE_TYPE.CTD_ANALYTICS_BUYERS : EXPORT_TABLE_TYPE.CTD_ANALYTICS_SUPPLIERS,
        );
      case CompanyTradeDataTab.PortsOfDelivery:
        return replace(
          get(URL_EXPORT_MAPPING, isBuyer ? EXPORT_TABLE_TYPE.CTD_POD_BUYERS : EXPORT_TABLE_TYPE.CTD_POD_SUPPLIERS),
          ':id',
          portCode ?? '',
        );
      case CompanyTradeDataTab.PortsOfOrigin:
        return replace(
          get(URL_EXPORT_MAPPING, isBuyer ? EXPORT_TABLE_TYPE.CTD_POO_BUYERS : EXPORT_TABLE_TYPE.CTD_POO_SUPPLIERS),
          ':id',
          portCode ?? '',
        );
      default:
        return '';
    }
  }, [tab, type, portCode]);

  // const exportFeatureId = useMemo(() => {
  //   switch (tab) {
  //     case CompanyTradeDataTab.Analytics:
  //       return LIST_FEATURES.COMPANY_PROFILE_DATA_ANALYTICS;
  //     case CompanyTradeDataTab.PortsOfDelivery:
  //       return LIST_FEATURES.COMPANY_PROFILE_PORTS_OF_DELIVERY;
  //     case CompanyTradeDataTab.PortsOfOrigin:
  //       return LIST_FEATURES.COMPANY_PROFILE_PORTS_OF_ORIGIN;
  //     default:
  //       return LIST_FEATURES.COMPANY_PROFILE_DATA_ANALYTICS;
  //   }
  // }, [tab]);

  const configColumn: ConfigCell<ICTDBuyerSuppliersList>[] = [
    {
      disablePadding: false,
      id: 'company_name',
      label: 'Companies',
      cell: (data: ICTDBuyerSuppliersList) => (
        <Stack direction="row" spacing={1}>
          <FlagCountry code={data?.company_country_code || ''} />
          <LinkToCompanyDetail
            id={data?.company_id}
            companyName={data.company_name || ''}
            params={queryParams}
            viewedAsType={type}
          />
        </Stack>
      ),
    },
    {
      disablePadding: false,
      id: 'bol',
      label: 'Number of B/L',
      cell: (data: ICTDBuyerSuppliersList) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.bol)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: 'Weight',
      cell: (data: ICTDBuyerSuppliersList) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.weight)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: 'Quantity',
      cell: (data: ICTDBuyerSuppliersList) => (
        <Typography variant="body2" color={'text.secondary'}>
          {renderValueNumberTrade(data.quantity)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      // label: t('market_analytics.table.column_name.value'),
      label: (
        <Stack direction={'row'} gap={1} alignItems={'center'}>
          <Box>Value (USD)</Box>
          <TooltipFormLabel
            title={`Important Note: Not all countries provide unit or total dollar values. Total values will be inaccurate when calculated collectively across all countries. Please refer to individual bills of lading for detailed research`}
          />
        </Stack>
      ),
      cell: (data: ICTDBuyerSuppliersList) => {
        const checkRestrictionValue = checkRestriction({
          countryCode: data?.company_country_code || '',
          typeData: LIST_RESTRICTION_DATA.VALUES_USD,
        });
        return (
          <Typography variant="body2" color={'text.secondary'}>
            {formatValueUSD(data.value_usd)}
            {checkRestrictionValue && <TooltipTradeDataLimit />}
          </Typography>
        );
      },
    },
  ];

  return (
    <ModalComponent padding={0} pt={0} handleClose={() => handleOpenChange(false)} open={open}>
      <Stack direction="column" spacing={3} width="80vw" bgcolor={background.paper} p={4}>
        <Stack alignItems={'center'} direction="row" justifyContent="space-between">
          <Typography variant="h5">{startCase(type)} List</Typography>
          <Typography variant="body2" color="text.secondary">
            This is a list of {type === TypeBuyerSupplier.supplier ? 'suppliers' : 'buyers'} with keyword {keyword} from{' '}
            {dayjs(startDate).format(FORMAT_DATE_TIME.LONG_MONTH_DAY_YEAR)} until{' '}
            {dayjs(endDate).format(FORMAT_DATE_TIME.LONG_MONTH_DAY_YEAR)}
          </Typography>
          <ExportDataTable
            fileName={`ctd-${type}-list`}
            featureId={LIST_FEATURES.INTERNAL_COMPANY_PROFILE_SUB_LIST_SUPPLIERS_BUYERS}
            endpoint={exportEndpoint}
            paramsExport={mergedPayload}
            totalRecordsAvailable={data?.total_records || 0}
          />
        </Stack>
        <CustomTable
          dataKey={(item) => item.company_country_code}
          loading={isFetching}
          maxRow={10}
          onChangePage={(value) => setParams({ ...params, offset: value })}
          handleSort={(value) => setSort('ctdBuyersSuppliersList', value)}
          onChangePageSize={(value) => setParams({ offset: 0, limit: value })}
          page={params.offset}
          pageSize={params.limit}
          data={data?.data ?? []}
          totalRecords={data?.total_records}
          configColumn={configColumn}
        />
      </Stack>
    </ModalComponent>
  );
};

export default PopupBuyersSuppliersList;
